import { MouseEvent, ReactNode } from 'react';

import { HelpIcon } from '@braskai/icons';
import { useDeviceType } from '@braskai/ui-kit';

import { Box, Stack, Typography } from '@mui/material';

import { FEATURES_KEYS, FEATURES_MAP } from '@/pages/PricingPage/constants';

import { FeatureTooltip } from '@/entities/pricing';

import { BoosterType } from '@/shared/types/Billing';
import { Switch } from '@/shared/ui/Switch';

type BoosterItemProps = {
  title: string;
  description: string;
  disabled?: boolean;
  isChecked: boolean;
  price: string;
  rightContent?: ReactNode;
  scheduledChangeText?: string;
  type: BoosterType;
  onChange: () => void;
};

const boosterItemToFeatureTooltipMap = {
  [BoosterType.SRT]: FEATURES_MAP[FEATURES_KEYS.srtDownloadUpload],
  [BoosterType.TeamspaceSeat]: FEATURES_MAP[FEATURES_KEYS.teamspacesBusiness],
  [BoosterType.TranslationPrompting]: FEATURES_MAP[FEATURES_KEYS.userPrompting],
};

export const BoosterItem = ({
  title,
  description,
  disabled,
  isChecked,
  price,
  rightContent,
  scheduledChangeText,
  type,
  onChange,
}: BoosterItemProps) => {
  const { isMobile } = useDeviceType();

  const handleTextClick = (e: MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <Box
      sx={{
        backgroundColor: 'grey.50',
        border: '1px solid',
        borderColor: 'grey.200',
        borderRadius: '12px',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        padding: '16px',
        pointerEvents: disabled ? 'none' : 'auto',
        transition: 'all 0.2s ease-in-out',
      }}
      onClick={onChange}
    >
      <Stack alignItems="stretch" direction="row" justifyContent="space-between">
        <Stack alignItems="flex-start" direction="row" gap={8}>
          <Switch checked={isChecked} disabled={disabled} sx={{ pointerEvents: 'none' }} />
          <Stack>
            <Stack onClick={handleTextClick}>
              <FeatureTooltip tooltipProps={boosterItemToFeatureTooltipMap[type].tooltip}>
                <Typography color="grey.900" variant="body-2-md">
                  {title} {isMobile && <HelpIcon height={14} width={14} />}
                </Typography>
              </FeatureTooltip>
            </Stack>

            <Typography color="grey.500" maxWidth={isMobile ? 250 : 270} textAlign="left" variant="body-2">
              {description}
            </Typography>
            {scheduledChangeText && (
              <Typography color="grey.500" maxWidth={isMobile ? 250 : 270} mt={20} variant="body-2">
                {scheduledChangeText}
              </Typography>
            )}
          </Stack>
        </Stack>

        <Stack alignItems="flex-end" flexDirection="column" justifyContent="space-between">
          <Typography color="grey.900" textAlign="right" variant="body-2-md" whiteSpace="nowrap">
            {price}
          </Typography>
          <Stack>
          {rightContent}
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
};
